import React, { useState, useRef, useEffect } from "react";
import { FaPlus, FaRecycle, FaComments } from "react-icons/fa";
import theme from "../../styles/theme/theme";
import UrgentScrapRequest from "./UrgentScrapRequest";
import SupportChat from "./SupportChat";

const FloatingActions = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activePanel, setActivePanel] = useState(null);
  const [pulse, setPulse] = useState(false);  // New state to trigger pulse animation
  const closeTimer = useRef(null);

  // HOVER OPEN
  const handleMouseEnter = () => {
    if (!activePanel) {
      if (closeTimer.current) clearTimeout(closeTimer.current);
      setMenuOpen(true);
    }
  };

  // DELAY CLOSE
  const handleMouseLeave = () => {
    if (activePanel) return; // keep open if panel active
    if (closeTimer.current) clearTimeout(closeTimer.current);

    closeTimer.current = setTimeout(() => {
      setMenuOpen(false);
    }, 300);
  };

  // CLICK PANEL
  const togglePanel = (panel) => {
    setActivePanel((prev) => (prev === panel ? null : panel));
    setMenuOpen(true); // keep menu open
  };

  // CLOSE PANEL
  const closePanel = () => {
    setActivePanel(null);
    setMenuOpen(false);
  };

  // Pulsing animation interval
  useEffect(() => {
    const interval = setInterval(() => {
      setPulse((prev) => !prev);  // Toggle pulse state every few seconds
    }, 1000); // Every 5 seconds

    return () => clearInterval(interval);  // Clean up on unmount
  }, []);

  return (
    <>
      {/* Floating Actions */}
      <div
        className="fab-container"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Submenu (always mounted) */}
        <div className={`fab-actions ${menuOpen || activePanel ? "show" : ""}`}>
          <div className="fab-item">
            <span className="fab-label">Urgent Scrap Request</span>
            <button
              className="fab-sub"
              style={{ background: theme.palette.tertiary.main }}
              onClick={() => togglePanel("urgent")}
            >
              <FaRecycle />
            </button>
          </div>

          <div className="fab-item">
            <span className="fab-label">Support Chat</span>
            <button
              className="fab-sub"
              style={{ background: theme.palette.primary.main }}
              onClick={() => togglePanel("chat")}
            >
              <FaComments />
            </button>
          </div>
        </div>

        {/* Main FAB with pulsing animation */}
        <button
          className={`fab-main ${(menuOpen || activePanel) ? "open" : ""} ${pulse ? "pulse" : ""}`}
          onClick={closePanel}
          style={{ background: theme.palette.info.main }}
        >
          <FaPlus />
        </button>
      </div>

      {/* ⭐ PANEL WRAPPED TO FORCE IT ABOVE EVERYTHING ⭐ */}
      <div className="floating-panel">
        <UrgentScrapRequest
          show={activePanel === "urgent"}
          onHide={closePanel}
        />

        <SupportChat
          show={activePanel === "chat"}
          onHide={closePanel}
          showAlert={(msg, type) => alert(`${type}: ${msg}`)}
        />
      </div>

      {/* CSS */}
      <style>{`
        .fab-container {
          position: fixed;
          right: 24px;
          bottom: 24px;
          z-index: 1600;
        }

        .fab-actions {
          position: absolute;
          bottom: 75px;
          right: 0;
          display: none;
          flex-direction: column;
          align-items: flex-end;
          gap: 12px;
          opacity: 0;
          transform: translateY(10px);
          transition: opacity 0.25s ease, transform 0.25s ease;
        }

        .fab-actions.show {
          display: flex;
          opacity: 1;
          transform: translateY(0);
        }

        .fab-main, .fab-sub {
          width: 60px;
          height: 60px;
          border-radius: 50%;
          border: none;
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 6px 18px rgba(0,0,0,0.18);
          cursor: pointer;
          transition: all 0.3s ease;
          font-size: 24px;
        }

        .fab-main {
          position: absolute;
          bottom: 0;
          right: 0;
        }

        .fab-main.open {
          transform: rotate(45deg);
        }

        .fab-item {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .fab-label {
          background: #333;
          color: #fff;
          padding: 6px 12px;
          border-radius: 8px;
          font-size: 13px;
          white-space: nowrap;
          opacity: 0;
          transform: translateX(10px);
          transition: all 0.25s ease;
          box-shadow: 0 3px 12px rgba(0,0,0,0.2);
        }

        .fab-actions.show .fab-label {
          opacity: 1;
          transform: translateX(0);
        }

        /* ⭐ FORCE PANELS TO APPEAR ABOVE ALL BUTTONS ⭐ */
        .floating-panel {
          position: relative;
          z-index: 3000 !important;
        }

        /* ⭐ Pulsing animation for the main button ⭐ */
        .fab-main.pulse {
          animation: pulseEffect 1.5s infinite;
        }

        @keyframes pulseEffect {
          0% {
            transform: scale(1);
            box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
          }
          50% {
            transform: scale(1.1);
            box-shadow: 0 0 20px rgba(0, 0, 0, 0.4);
          }
          100% {
            transform: scale(1);
            box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
          }
        }

        @media (max-width: 768px) {
          .fab-main, .fab-sub {
            width: 56px;
            height: 56px;
            font-size: 20px;
          }
        }

        @media (max-width: 480px) {
          .fab-main, .fab-sub {
            width: 52px;
            height: 52px;
            font-size: 18px;
          }
        }
      `}</style>
    </>
  );
};

export default FloatingActions;
